/**
 * Gets the base URL for the application with fallback chain:
 * 1. VITE_BAANA_URL (primary env variable)
 * 2. Runtime origin (globalThis.location.origin)
 * 3. https://baana.localdev:3000 (default - matches local dev server config)
 *
 * Validates and normalizes URLs to ensure they're absolute with protocol and no trailing slash.
 * Logs warnings when falling back for SSR/CI debugging.
 */
const getBaseUrl = (): string => {
  const DEFAULT_BASE_URL = "https://baana.localdev:3000";

  const isValidUrl = (url: string | undefined): url is string => {
    if (!url?.trim()) return false;
    try {
      const parsed = new URL(url);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      return false;
    }
  };

  const normalize = (url: string): string => {
    let normalized = url.trim();
    if (!/^https?:\/\//i.exec(normalized)) normalized = `http://${normalized}`;
    // Remove trailing slashes: find last non-slash index (safe, O(n) single pass)
    let endIndex = normalized.length;
    while (endIndex > 0 && normalized[endIndex - 1] === "/") {
      endIndex--;
    }
    return normalized.slice(0, endIndex);
  };

  const envUrl = import.meta.env.VITE_BAANA_URL;
  const runtimeOrigin = globalThis.location?.origin ?? "";

  if (isValidUrl(envUrl)) return normalize(envUrl);

  if (isValidUrl(runtimeOrigin)) {
    console.warn(
      "[baseUrlUtils] VITE_BAANA_URL not set or invalid, falling back to runtime origin:",
      runtimeOrigin
    );
    return normalize(runtimeOrigin);
  }

  console.warn(
    "[baseUrlUtils] No valid base URL found, using default:",
    DEFAULT_BASE_URL
  );
  return normalize(DEFAULT_BASE_URL);
};

export { getBaseUrl };
